/**
 * Right-sidebar attribute panel. Shows the registry-driven form for the
 * currently selected concept. Mutations are debounced through
 * patchConcept(); local store updates immediately, the API call follows
 * after 300ms of inactivity.
 *
 * Bilingual fields (name, description) render as a side-by-side EN + NL
 * pair. Type-specific attributes come from the registry schema. Steward-
 * only fields (name, type) are read-only when the user isn't owner/admin.
 */
import {
  type Concept,
  type Diagram,
  deriveDependentAttributes,
  derivedMultiplicityFor,
  deriveManifestationLevel,
  isCompatStereotype,
  MULTIPLICITY_SOURCE_KEY,
  MULTIPLICITY_TARGET_KEY,
  multiplicityGovernance,
  type NodeStyle,
} from "@ae/domain";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { GlossaryInfoButton } from "../../help/uiGlossary";
import {
  alwaysAllowedRelationTypeIds,
  getRegistry,
  relevantResolversForRelationType,
  relevantResolversForType,
  resolveBilingual,
  validRelationTypesBetween,
  validRelationTypesReversed,
} from "../../registry";
import { typeLabelFor } from "../../registry/typeLabel";
import { isAdminOfWorkspace, useSession } from "../../state/session";
import { useUi } from "../../state/ui";
import { useWorkspaceData } from "../../state/workspace";
import { hasArchimateSvgIcon } from "../canvas/archimateIconLibrary";
import { getArchimateShape } from "../canvas/archimateShapes";
import { SwatchColorPicker } from "../canvas/SwatchColorPicker";
import { PanelCollapseIcon } from "../PanelCollapseIcon";
import { usePanelWidth } from "../usePanelWidth";
import { AltLabelsSection } from "./AltLabelsSection";
import { BilingualField } from "./BilingualField";
import { CweLibraryPicker } from "./CweLibraryPicker";
import { AttributeField } from "./fields";
import { HistoryDialog } from "./HistoryDialog";
import { lockTargetFor } from "./lockTarget";
import { useObjectLock } from "./useObjectLock";

/** Minimal escape voor attribuut-selectoren — node ids zijn UUIDs, dus
 *  alleen hyphens / quotes / backslashes hoeven gequot't. */
function cssEscape(s: string): string {
  return s.replace(/(["'\\])/g, "\\$1");
}

export function AttributePanel() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const selectedConceptId = useUi((s) => s.selectedConceptId);
  const selectedRelationId = useUi((s) => s.selectedRelationId);
  const selectedRelationInstanceId = useUi((s) => s.selectedRelationInstanceId);
  const selectedDiagramId = useUi((s) => s.selectedDiagramId);
  const open = useUi((s) => s.attributePanelOpen);
  const setOpen = useUi((s) => s.setAttributePanelOpen);
  const { width: panelWidth, ResizeHandle } = usePanelWidth(
    "ae.panelWidth.attributePanel",
    288,
    "left",
  );
  const me = useSession((s) => s.me);
  const workspaceId = useSession((s) => s.workspaceId);
  // Alleen kijken: het paneel toont alles, maar elk veld staat uit.
  const readOnly = useSession((s) => s.readOnly);
  const branchId = useSession((s) => s.branchId);
  // Subscribe to revision so the panel re-reads on store changes.
  const revision = useWorkspaceData((s) => s.revision);
  void revision;
  // Actief diagram — nodig voor de per-instance opslag-context (vlag).
  const activeDiagramId = useWorkspaceData((s) => s.activeDiagramId);
  // Lokaal — opent boven de canvas een fullscreen-dialoog. Niet via
  // ui-store omdat 'ie alleen relevant is in deze component.
  // BELANGRIJK: hook MOET vóór elke early-return staan, anders crasht
  // React met "Rendered more hooks than during the previous render"
  // zodra de selection-state wijzigt (Rules of Hooks).
  const [historyOpen, setHistoryOpen] = useState(false);

  // Panel-zijde-detectie: standaard rechts (`right-2`), maar als 't
  // geselecteerde concept/relatie binnen het rechterstrook van de
  // canvas-area valt waar 't paneel zou landen, flippen we naar links.
  // De berekening is t.o.v. de panel-parent (canvas-inner-div), niet
  // het viewport — zo komt 'm bij left-mode netjes binnen de
  // modelleer-rechthoek terecht en niet over Explorer / TypePalette.
  const selectedInstanceId = useUi((s) => s.selectedInstanceId);
  const [panelSide, setPanelSide] = useState<"left" | "right">("right");
  const panelRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!open) return;
    if (typeof document === "undefined") return;
    const targetKey = selectedInstanceId ?? selectedConceptId ?? selectedRelationId;
    if (!targetKey) return;
    // React Flow gebruikt `.react-flow__node[data-id="X"]` voor nodes
    // en `.react-flow__edge[data-id="X"]` voor edges. CanvasGridLayout
    // gebruikt `[data-instance-id="X"]` voor BMC-slot-cards.
    const measure = () => {
      const el =
        document.querySelector(`.react-flow__node[data-id="${cssEscape(targetKey)}"]`) ??
        document.querySelector(`.react-flow__edge[data-id="${cssEscape(targetKey)}"]`) ??
        document.querySelector(`[data-instance-id="${cssEscape(targetKey)}"]`);
      if (!el) return;
      const rect = (el as HTMLElement).getBoundingClientRect();
      const parentRect = panelRef.current?.parentElement?.getBoundingClientRect();
      // Fallback voor de eerste-render-cyclus voordat de ref is gezet:
      // gebruik viewport. Volgende rAF-pass meet alsnog tegen parent.
      const refLeft = parentRect?.left ?? 0;
      const refRight = parentRect?.right ?? window.innerWidth;
      const buffer = 24;
      const panelLeftEdge = refRight - panelWidth - 8 - buffer;
      const targetRight = rect.left + rect.width;
      setPanelSide(targetRight > panelLeftEdge ? "left" : "right");
      void refLeft;
    };
    // DOM kan net-na-mounten nog niet beschikbaar zijn (rerender-cycle
    // van React Flow); twee passes: synchroon én post-paint via rAF
    // zodat we ook nieuw-geplaatste nodes correct meten.
    measure();
    const raf = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(raf);
  }, [open, selectedConceptId, selectedRelationId, selectedInstanceId, panelWidth]);

  // Een dubbelklik selecteert tekst — dat is browsergedrag, en het paneel dat
  // erdoor opengaat valt binnen die selectie. Resultaat: een paneel dat
  // volledig blauw oplicht tot je ergens klikt. De selectie hoort bij de
  // dubbelklik, niet bij het paneel, dus die halen we weg zodra 'ie opengaat.
  // Alleen de collapse: staat de cursor in een invoerveld, dan is de selectie
  // van de gebruiker en blijven we eraf.
  useEffect(() => {
    if (!open) return;
    if (typeof window === "undefined") return;
    const actief = document.activeElement;
    if (
      actief instanceof HTMLInputElement ||
      actief instanceof HTMLTextAreaElement ||
      (actief instanceof HTMLElement && actief.isContentEditable)
    ) {
      return;
    }
    const selectie = window.getSelection();
    if (selectie && !selectie.isCollapsed) selectie.removeAllRanges();
  }, [open, selectedConceptId, selectedRelationId, selectedDiagramId]);

  // Click-outside-to-close + Escape-to-close. Geldt voor zowel
  // canvas-geopende panels als Explorer-geopende panels.
  //
  // Drie event-bronnen voor robuustheid op alle render-paden:
  //   - `pointerdown` — modern, dekt muis + touch + pen
  //   - `mousedown` — desktop fallback wanneer pointerdown door een
  //     intermediate handler wordt onderdrukt (eerder gezien op BMC
  //     Canvas-cards die `draggable` zijn — de drag-start-detection
  //     van de browser absorbeerde de pointerdown soms voordat 't
  //     document de event zag)
  // Beiden geregistreerd in capture-phase (`true`) zodat we 'm vóór
  // andere handlers oppikken en `event.stopPropagation()` op
  // diepere nodes ons niet kan blokkeren.
  useEffect(() => {
    if (!open) return;
    if (typeof document === "undefined") return;
    const onOutsideDown = (e: Event) => {
      const target = e.target as Node | null;
      if (target && panelRef.current?.contains(target)) return;
      // Popovers/dropdowns die naar `document.body` portallen markeren
      // zichzelf met `data-swatch-popover` (kleurkiezer) of vergelijkbare
      // attribuut. Clicks daar binnen mogen het AttributePanel NIET
      // sluiten, anders verliest de gebruiker z'n selectie zodra 'ie
      // een kleur kiest. Capture-phase fires vóór de portal's onMouseDown,
      // dus stopPropagation lost dit niet op — we filteren expliciet.
      if (target instanceof Element && target.closest("[data-swatch-popover]")) return;
      // Elementen gemarkeerd met `[data-keep-attribute-panel-open]` mogen
      // het paneel niet sluiten. Gebruikt op de editor-header zodat
      // NL/EN-toggle, SettingsMenu, RepositoryMenu, etc. de selectie +
      // het open paneel intact laten — anders verliest de gebruiker
      // z'n bewerkings-context bij elke UI-tab/toggle-klik.
      if (target instanceof Element && target.closest("[data-keep-attribute-panel-open]")) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      // Escape sluit alleen 't paneel; geen stopPropagation zodat
      // andere Escape-consumers (zoekveld-clear, dropdowns) hun eigen
      // gedrag houden.
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onOutsideDown, true);
    document.addEventListener("mousedown", onOutsideDown, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onOutsideDown, true);
      document.removeEventListener("mousedown", onOutsideDown, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, setOpen]);

  const reg = getRegistry();
  const concept = selectedConceptId
    ? useWorkspaceData.getState().store.getConcept(selectedConceptId)
    : null;
  const relation = selectedRelationId
    ? useWorkspaceData.getState().store.getRelation(selectedRelationId)
    : null;
  const diagram = selectedDiagramId
    ? useWorkspaceData.getState().store.getDiagram(selectedDiagramId)
    : null;

  // ── Per-instance opslag-context (relatie) ──────────────────────────
  // Zie docs/architecture/per-instance-attribute-flag.md. Eén vlag
  // bepaalt of álle attribuut-waarden op de instance (viewState-entry)
  // of op de entity (relation.attributes) leven.
  const relInstanceVe =
    relation && activeDiagramId && selectedRelationInstanceId
      ? useWorkspaceData.getState().store.getDiagram(activeDiagramId)?.viewState[
          selectedRelationInstanceId
        ]
      : undefined;
  const relInstanceBag =
    (relInstanceVe?.stereotypeAttributes as Record<string, unknown> | undefined) ?? {};
  const relHasInstanceContext = !!(
    relation &&
    selectedRelationInstanceId &&
    activeDiagramId &&
    relInstanceVe
  );
  // Effectieve vlag: expliciet gezet, anders afgeleid van "heeft deze
  // instance al een attribuut-bag?" — zo blijven bestaande (legacy
  // scope:instance) instances hun instance-waarden lezen zonder migratie.
  const relStoreOnInstance = relHasInstanceContext
    ? (relInstanceVe?.storeOnInstance ?? Object.keys(relInstanceBag).length > 0)
    : false;

  /**
   * Micro-slot op wat je werkelijk bewerkt.
   *
   * Staan de waarden op het **voorkomen** (instance-opslag), dan hoort het
   * slot op de viewState-sleutel en niet op het object: twee mensen op twee
   * verschillende voorkomens van hetzelfde object horen elkaar niet in de weg
   * te zitten. Staan ze op het object, dan is het object de korrel.
   *
   * Waarom hier een slot en niet de teller die het verslepen bewaakt: een
   * teller grijpt ná afloop in. Voor een versleping is dat prima — één gebaar,
   * je ziet meteen wat er gebeurde. Voor het invullen van attribuutwaarden is
   * het te laat: dan heb je al een halve minuut zitten typen. Zie
   * `docs/architecture/gelijktijdigheid.md`.
   *
   * Vóór de vroege return hieronder, want een hook mag niet voorwaardelijk zijn.
   */
  const lock = useObjectLock(
    lockTargetFor({
      storeOnInstance: relStoreOnInstance,
      instanceId: selectedRelationInstanceId,
      conceptId: selectedConceptId,
      relationId: selectedRelationId,
    }),
  );

  // Undo-snapshot: vastgelegd bij selectie-wissel (niet bij elke edit),
  // gebruikt door de "Herstel mutaties"-knop.
  const restoreSnapshotRef = useRef<{
    relAttrs: Record<string, unknown>;
    bag: Record<string, unknown> | undefined;
    flag: boolean | undefined;
  } | null>(null);
  // Snapshot bewust alleen bij selectie-wissel (niet bij elke edit).
  useEffect(() => {
    if (!relation) {
      restoreSnapshotRef.current = null;
      return;
    }
    restoreSnapshotRef.current = {
      relAttrs: { ...relation.attributes },
      bag: relInstanceVe?.stereotypeAttributes
        ? { ...(relInstanceVe.stereotypeAttributes as Record<string, unknown>) }
        : undefined,
      flag: relInstanceVe?.storeOnInstance,
    };
  }, [selectedRelationId, selectedRelationInstanceId, activeDiagramId]);

  // Popup only renders when there's actually something to show AND the
  // user hasn't manually dismissed it. selectInstance / selectRelation /
  // selectDiagram re-open it automatically (see UI store), so closing +
  // re-clicking a concept brings the popup back.
  if (!open || (!concept && !relation && !diagram)) {
    return null;
  }

  const elementType = concept ? reg.getElementType(concept.typeId) : null;

  const ownerCanEdit = !concept || !me || concept.ownerEmail === me.email;
  const userIsAdmin = isAdminOfWorkspace(me, workspaceId);
  // Een slot van een collega (of van een lopende import) zet bewerken uit —
  // ongeacht eigenaarschap. Zie useObjectLock.
  const stewardEditable = (ownerCanEdit || userIsAdmin) && lock.heldBy === null;

  const headerLabel = concept
    ? t("attributePanel.headerConcept")
    : relation
      ? t("attributePanel.headerRelation")
      : diagram
        ? t("attributePanel.headerDiagram")
        : t("attributePanel.headerEmpty");

  // ── Relatie-attribuut lees/schrijf-routing via de instance-vlag ─────
  const readRelAttr = (name: string): unknown => {
    if (!relation) return undefined;
    return relStoreOnInstance
      ? (relInstanceBag[name] ?? relation.attributes[name])
      : relation.attributes[name];
  };
  const writeRelAttr = (name: string, value: unknown) => {
    if (!relation || !workspaceId || !branchId) return;
    if (
      relStoreOnInstance &&
      relHasInstanceContext &&
      activeDiagramId &&
      selectedRelationInstanceId &&
      relInstanceVe
    ) {
      const nextBag = {
        ...(relInstanceVe.stereotypeAttributes ?? {}),
        [name]: value,
        // Afgeleide native multipliciteit landt in dezelfde bag als z'n bron,
        // anders staat de Sharp-cardinaliteit per instance en de afgeleide
        // waarde op modelniveau — twee plekken die uit elkaar kunnen lopen.
        ...derivedMultiplicityFor(name, value),
      };
      useWorkspaceData
        .getState()
        .applyViewElementOnCanvas(
          workspaceId,
          branchId,
          activeDiagramId,
          selectedRelationInstanceId,
          {
            ...relInstanceVe,
            stereotypeAttributes: nextBag,
          },
        );
      return;
    }
    void useWorkspaceData.getState().patchRelation(workspaceId, branchId, relation.id, {
      attributes: { ...relation.attributes, [name]: value, ...derivedMultiplicityFor(name, value) },
    });
  };
  const setRelStoreOnInstance = (next: boolean) => {
    if (
      !relation ||
      !workspaceId ||
      !branchId ||
      !relHasInstanceContext ||
      !activeDiagramId ||
      !selectedRelationInstanceId ||
      !relInstanceVe
    )
      return;
    const ws = useWorkspaceData.getState();
    if (next) {
      // Nee → Ja: kopieer huidige entity-waarden als startpunt naar de
      // instance-bag (bestaande instance-waarden blijven er bovenop).
      const startBag = {
        ...relation.attributes,
        ...(relInstanceVe.stereotypeAttributes ?? {}),
      };
      ws.applyViewElementOnCanvas(
        workspaceId,
        branchId,
        activeDiagramId,
        selectedRelationInstanceId,
        {
          ...relInstanceVe,
          storeOnInstance: true,
          stereotypeAttributes: startBag,
        },
      );
    } else {
      // Ja → Nee: de lokale instance-waarden gaan verloren. We schrijven
      // BEWUST niets terug naar de entity — dat voorkomt elke
      // inconsistentie. Eerst waarschuwen, dan de instance-bag wissen
      // zodat de relatie-waarden de bron worden.
      const msg = t("attributePanel.instance.loseLocalValuesConfirm");
      if (typeof window !== "undefined" && !window.confirm(msg)) return;
      ws.applyViewElementOnCanvas(
        workspaceId,
        branchId,
        activeDiagramId,
        selectedRelationInstanceId,
        {
          ...relInstanceVe,
          storeOnInstance: false,
          stereotypeAttributes: undefined,
        },
      );
    }
  };
  const handleRestoreRelation = () => {
    const snap = restoreSnapshotRef.current;
    if (!snap || !relation || !workspaceId || !branchId) return;
    const ws = useWorkspaceData.getState();
    void ws.patchRelation(workspaceId, branchId, relation.id, { attributes: snap.relAttrs });
    if (relHasInstanceContext && activeDiagramId && selectedRelationInstanceId && relInstanceVe) {
      ws.applyViewElementOnCanvas(
        workspaceId,
        branchId,
        activeDiagramId,
        selectedRelationInstanceId,
        {
          ...relInstanceVe,
          storeOnInstance: snap.flag,
          stereotypeAttributes: snap.bag,
        },
      );
    }
  };

  return (
    <aside
      ref={panelRef}
      data-scope="attribute-panel"
      // Het slot wordt gepakt zodra de focus in een veld belandt en losgelaten
      // kort nadat 'ie het paneel verlaat — springen tussen twee velden mag
      // 'm niet opheffen (zie RELEASE_DELAY_MS).
      onFocusCapture={readOnly ? undefined : lock.begin}
      onBlurCapture={lock.endSoon}
      // Floating popup binnen de canvas-inner-div (Editor.tsx). Beslaat
      // de rechter- of linkerstrook van de canvas-area — niet van het
      // hele venster, zodat 'ie bij flip naar links niet over Explorer
      // / TypePalette heen valt. Sluit zichzelf via outside-click-
      // listener of Escape (zie useEffect hierboven).
      className={`ledger absolute top-2 z-30 flex max-h-[calc(100%-1rem)] flex-col overflow-hidden rounded-md border border-(--ld-control) bg-(--ld-surface) shadow-2xl ${
        panelSide === "left" ? "left-2" : "right-2"
      }`}
      style={{ width: panelWidth }}
    >
      <header className="flex items-center justify-between border-(--ld-border) border-b px-3 py-2">
        <h2 className="font-semibold text-(--ld-text) text-sm">{headerLabel}</h2>
        <div className="flex items-center gap-1">
          <GlossaryInfoButton
            termIds={[
              "concept",
              "stereotype",
              "facet_reference",
              "definition",
              "attribute",
              "identifier",
              "attribute_type",
              "business_object",
              "business_actor",
              "business_role",
              "reification",
            ]}
          />
          {(concept || relation) && (
            <button
              type="button"
              className="rounded px-1.5 py-0.5 text-(--ld-muted) text-sm hover:bg-(--ld-raised) hover:text-(--ld-text)"
              onClick={() => setHistoryOpen(true)}
              title={t("attributePanel.historyTitle")}
              aria-label={t("attributePanel.historyAria")}
            >
              📜
            </button>
          )}
          <button
            type="button"
            className="flex h-7 w-7 items-center justify-center rounded text-(--ld-text) text-base leading-none hover:bg-(--ld-active)"
            onClick={() => setOpen(false)}
          >
            <PanelCollapseIcon />
          </button>
        </div>
      </header>

      {lock.lost && !lock.heldBy && (
        <div className="border-transparent border-b bg-(--ld-notice-bg) px-3 py-1.5 text-xs text-(--ld-notice-ink)">
          {t("locks.lost")}
        </div>
      )}
      {lock.heldBy && (
        <div className="border-transparent border-b bg-(--ld-notice-bg) px-3 py-1.5 text-xs text-(--ld-notice-ink)">
          {lock.byBatch ? t("locks.heldByBatch") : t("locks.heldBy", { holder: lock.heldBy })}
        </div>
      )}

      {concept && (
        <HistoryDialog
          kind="concept"
          conceptId={concept.id}
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
        />
      )}
      {relation && !concept && (
        <HistoryDialog
          kind="relation"
          relationId={relation.id}
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
        />
      )}

      {/* Een uitgeschakelde fieldset zet elk veld en elke knop erin uit: met
          alleen leesrecht kijk je (Gerald, 2026-10-17). */}
      <fieldset
        disabled={readOnly}
        className="m-0 min-w-0 flex-1 overflow-y-auto border-0 px-3 py-3"
      >
        {!concept && !relation && !diagram ? (
          <p className="text-(--ld-muted) text-xs">{t("attributePanel.emptyHint")}</p>
        ) : relation ? (
          <div className="flex flex-col gap-3">
            {relHasInstanceContext && (
              <section
                className={`flex flex-col gap-1.5 rounded border px-2 py-2 ${
                  relStoreOnInstance
                    ? "border-(--ld-notice-ink)/40 bg-(--ld-notice-bg)"
                    : "border-(--ld-border) bg-(--ld-raised)"
                }`}
              >
                <label className="flex flex-col gap-0.5">
                  <span className="text-(--ld-muted) text-xs">
                    {t("attributePanel.instance.storeOnInstanceLabel")}
                  </span>
                  <select
                    className="w-full rounded border border-(--ld-muted) bg-(--ld-surface) px-2 py-1 text-xs focus:border-(--ld-accent) focus:outline-none"
                    value={relStoreOnInstance ? "true" : "false"}
                    onChange={(e) => setRelStoreOnInstance(e.target.value === "true")}
                  >
                    <option value="false">{t("attributePanel.instance.storeOnRelation")}</option>
                    <option value="true">{t("attributePanel.instance.storeOnThisInstance")}</option>
                  </select>
                </label>
                <button
                  type="button"
                  className="self-start rounded border border-(--ld-control) bg-(--ld-surface) px-2 py-0.5 text-(--ld-muted) text-xs hover:bg-(--ld-raised)"
                  onClick={handleRestoreRelation}
                  title={t("attributePanel.instance.revertTitle")}
                >
                  {t("attributePanel.instance.revertButton")}
                </button>
              </section>
            )}
            <section>
              <h3 className="mb-1 font-semibold text-(--ld-text) text-xs">
                {t("attributePanel.sectionType")}
              </h3>
              <RelationTypePicker
                relationTypeId={relation.typeId}
                sourceConceptId={relation.sourceConceptId}
                targetConceptId={relation.targetConceptId}
                lang={lang}
                onChange={(newTypeId, reversed) => {
                  if (!workspaceId || !branchId) return;
                  const patch: Parameters<
                    ReturnType<typeof useWorkspaceData.getState>["patchRelation"]
                  >[3] = { type_id: newTypeId };
                  if (reversed) {
                    patch.source_concept_id = relation.targetConceptId;
                    patch.target_concept_id = relation.sourceConceptId;
                  }
                  const ws = useWorkspaceData.getState();
                  void ws.patchRelation(workspaceId, branchId, relation.id, patch);
                  // When the endpoints swap, the source/target aanhechting
                  // in view_state moet meeswappen — anders blijft de lijn
                  // hangen op de oude anchor-punten (die nu bij de
                  // tegenovergestelde endpoint horen).
                  //
                  // Sinds we voor visuele consistentie alleen-angle gebruiken
                  // (zie commit canvas-handle-mismatch-fix), wisselen we
                  // hier source/targetAngle om. Eventueel oud-opgeslagen
                  // sourceHandle/targetHandle wissen we mee.
                  if (reversed) {
                    const diagramId = ws.activeDiagramId;
                    if (diagramId) {
                      const ve = ws.store.getDiagram(diagramId)?.viewState[relation.id];
                      ws.setRelationAnglesOnCanvas(
                        workspaceId,
                        branchId,
                        relation.id,
                        ve?.targetAngle ?? null,
                        ve?.sourceAngle ?? null,
                      );
                      if (ve?.sourceHandle || ve?.targetHandle) {
                        ws.setRelationHandlesOnCanvas(
                          workspaceId,
                          branchId,
                          relation.id,
                          null,
                          null,
                        );
                      }
                    }
                  }
                }}
              />
            </section>

            <section>
              <BilingualField
                kind="name"
                labelNl={t("attributePanel.labelNl")}
                labelEn={t("attributePanel.labelEn")}
                valueNl={relation.label.nl ?? ""}
                valueEn={relation.label.en ?? ""}
                onChange={(l, value) => {
                  if (!workspaceId || !branchId) return;
                  void useWorkspaceData
                    .getState()
                    .patchRelation(workspaceId, branchId, relation.id, {
                      label: { ...relation.label, [l]: value },
                    });
                }}
                onCommit={() => {
                  // Duplicaat-check pas op COMMIT (blur), niet per toetsaanslag.
                  if (!workspaceId || !branchId) return;
                  void useWorkspaceData
                    .getState()
                    .checkRelationDuplicate(workspaceId, branchId, relation.id);
                }}
              />
            </section>

            {/* Alt-labels per view-resolver (M8 §5) — voor relaties waarvan
                het type op ≥1 viewpoint met een label_resolver valt. */}
            {(() => {
              const resolvers = relevantResolversForRelationType(relation.typeId);
              if (resolvers.length === 0) return null;
              return (
                <section>
                  <AltLabelsSection
                    key={relation.id}
                    resolvers={resolvers}
                    altLabels={relation.altLabels}
                    onChange={(resolver, l, value) => {
                      if (!workspaceId || !branchId) return;
                      const current = relation.altLabels[resolver] ?? {};
                      void useWorkspaceData
                        .getState()
                        .patchRelation(workspaceId, branchId, relation.id, {
                          alt_labels: { [resolver]: { ...current, [l]: value } },
                        });
                    }}
                  />
                </section>
              );
            })()}

            {(() => {
              const relationType = reg.getRelationType(relation.typeId);
              const attrSchemas = relationType?.attributes ?? [];
              if (attrSchemas.length === 0) return null;
              // Governance op native multipliciteit (fase 7). De waarde staat
              // op de relatie, maar wie 'm mág zetten hangt af van het
              // relatietype en de eindpunten — zie multiplicity.ts.
              const govern = multiplicityGovernance({
                relationTypeId: relation.typeId,
                sourceTypeId: useWorkspaceData.getState().store.getConcept(relation.sourceConceptId)
                  ?.typeId,
                targetTypeId: useWorkspaceData.getState().store.getConcept(relation.targetConceptId)
                  ?.typeId,
                stereotypes: relation.stereotypes ?? [],
                // Op een Sharp-werkstroom is multipliciteit notatie en geen
                // annotatie; dan hoort er geen waarschuwing te staan.
                viewKindId: activeDiagramId
                  ? useWorkspaceData.getState().store.getDiagram(activeDiagramId)?.viewKind
                  : undefined,
              });
              const isMultiplicity = (name: string) =>
                name === MULTIPLICITY_SOURCE_KEY || name === MULTIPLICITY_TARGET_KEY;
              return (
                <section className="flex flex-col gap-2">
                  <h3 className="font-semibold text-(--ld-text) text-xs">
                    {t("attributePanel.sectionAttributes")}
                  </h3>
                  {govern === "discouraged" &&
                    attrSchemas.some((sc) => isMultiplicity(sc.name)) && (
                      <p className="rounded bg-(--ld-notice-bg) px-2 py-1 text-xs text-(--ld-notice-ink)">
                        {t("attributePanel.multiplicityDiscouraged")}
                      </p>
                    )}
                  {attrSchemas.map((schema) => {
                    // Richting een Junction sluit C260 §5.6 multipliciteit uit.
                    if (isMultiplicity(schema.name) && govern === "blocked") return null;
                    // Met een «Concept Association» erop volgt de waarde uit de
                    // Sharp-cardinaliteit — read-only, anders ontstaat er een
                    // tweede waarheid naast het contract.
                    if (isMultiplicity(schema.name) && govern === "derived") {
                      return (
                        <AttributeField
                          key={schema.name}
                          schema={schema}
                          value={readRelAttr(schema.name)}
                          disabled
                          lang={lang}
                          onChange={() => {
                            /* afgeleid uit de Sharp-cardinaliteit */
                          }}
                        />
                      );
                    }
                    return (
                      <AttributeField
                        key={schema.name}
                        schema={schema}
                        value={readRelAttr(schema.name)}
                        lang={lang}
                        onChange={(value) => writeRelAttr(schema.name, value)}
                      />
                    );
                  })}
                </section>
              );
            })()}

            {/* ── Relatie-stereotypes ──────────────────────────────────
                 Bv. Sharp's «ConceptAssociation» (op archimate.Association)
                 voegt forward_name / reverse_name / cardinaliteit-velden
                 toe. Verbergen wanneer er geen toepasbare stereotype is. */}
            {(() => {
              // Huidige stereotypes meegeven zodat conditionele
              // `applies_to`-entries (`requires_stereotype`) meetellen.
              const available = reg.stereotypesFor(relation.typeId, relation.stereotypes ?? []);
              if (available.length === 0) return null;
              // Sorteer alfabetisch op de getoonde label (bilingual-resolved),
              // niet op registry-volgorde — anders staan stereotypes door
              // elkaar afhankelijk van pack-load-order.
              const availableSorted = [...available].sort((a, b) =>
                resolveBilingual(a.label, lang).localeCompare(
                  resolveBilingual(b.label, lang),
                  lang,
                ),
              );
              const active = new Set(relation.stereotypes);
              const toggle = (stereotypeId: string) => {
                if (!workspaceId || !branchId) return;
                const next = active.has(stereotypeId)
                  ? relation.stereotypes.filter((s) => s !== stereotypeId)
                  : [...relation.stereotypes, stereotypeId];
                void useWorkspaceData
                  .getState()
                  .patchRelation(workspaceId, branchId, relation.id, { stereotypes: next });
              };
              return (
                <section className="flex flex-col gap-1">
                  <h3 className="font-semibold text-(--ld-text) text-xs">
                    {t("attributePanel.sectionStereotypes")}
                  </h3>
                  <div className="flex flex-col gap-0.5">
                    {availableSorted.map((st) => {
                      const checked = active.has(st.id);
                      return (
                        <label
                          key={st.id}
                          className={`flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-xs hover:bg-(--ld-raised) ${
                            checked ? "text-(--ld-text)" : "text-(--ld-muted)"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggle(st.id)}
                            className="h-3 w-3"
                          />
                          <span className={checked ? "font-semibold" : ""}>
                            {resolveBilingual(st.label, lang)}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </section>
              );
            })()}

            {/* Per-actief-stereotype een sectie met de typed attribuut-
                bundle — bv. bij «ConceptAssociation» landen forward_name,
                reverse_name + cardinaliteit-velden hier.
                Per-instance-attribuut-flag (2026-05-28): of deze waarden
                op de instance of op de relatie landen, wordt bepaald door
                de vlag bovenaan het paneel (readRelAttr/writeRelAttr) —
                niet langer per stereotype-scope. */}
            {relation.stereotypes.map((stereotypeId) => {
              const st = reg.getStereotype(stereotypeId);
              if (!st) return null;
              const attrs = reg.resolveStereotypeAttributes(st);
              if (attrs.length === 0) return null;
              return (
                <section key={`rel-st-attrs-${stereotypeId}`} className="flex flex-col gap-2">
                  <h3 className="font-semibold text-(--ld-text) text-xs">
                    {resolveBilingual(st.label, lang)}
                  </h3>
                  {attrs.map((schema) => (
                    <AttributeField
                      key={schema.name}
                      schema={schema}
                      value={readRelAttr(schema.name)}
                      lang={lang}
                      onChange={(value) => writeRelAttr(schema.name, value)}
                    />
                  ))}
                </section>
              );
            })}

            <section>
              <h3 className="mb-1 font-semibold text-(--ld-text) text-xs">
                {t("attributePanel.sectionLineRouting")}
              </h3>
              {(() => {
                const ws = useWorkspaceData.getState();
                const diagramId = ws.activeDiagramId;
                const ve = diagramId
                  ? ws.store.getDiagram(diagramId)?.viewState[relation.id]
                  : undefined;
                const current = ve?.routing ?? "straight";
                return (
                  <select
                    className="w-full rounded border border-(--ld-muted) bg-(--ld-surface) px-2 py-1 text-sm focus:border-(--ld-accent) focus:outline-none"
                    value={current}
                    onChange={(e) => {
                      if (!workspaceId || !branchId) return;
                      const v = e.target.value as
                        | "smoothstep"
                        | "step"
                        | "straight"
                        | "bezier"
                        | "manual";
                      useWorkspaceData
                        .getState()
                        .setRelationRoutingOnCanvas(workspaceId, branchId, relation.id, v);
                    }}
                  >
                    <option value="smoothstep">{t("attributePanel.routing.smoothstep")}</option>
                    <option value="step">{t("attributePanel.routing.step")}</option>
                    <option value="straight">{t("attributePanel.routing.straight")}</option>
                    <option value="bezier">{t("attributePanel.routing.bezier")}</option>
                    <option value="manual">{t("attributePanel.routing.manual")}</option>
                  </select>
                );
              })()}
            </section>

            <section>
              <h3 className="mb-1 font-semibold text-(--ld-text) text-xs">
                {t("attributePanel.sectionColours")}
              </h3>
              {(() => {
                const ws = useWorkspaceData.getState();
                const dId = ws.activeDiagramId;
                const ve = dId ? ws.store.getDiagram(dId)?.viewState[relation.id] : null;
                return (
                  <div className="flex flex-wrap items-center gap-3">
                    <NodeColorSlot
                      label={t("attributePanel.colourStroke")}
                      value={ve?.lineColor}
                      defaultPreview="#475569"
                      shape="line"
                      onChange={(c) => {
                        if (!workspaceId || !branchId) return;
                        ws.setRelationLineColorOnCanvas(
                          workspaceId,
                          branchId,
                          relation.id,
                          c ?? null,
                        );
                      }}
                    />
                    <NodeColorSlot
                      label={t("attributePanel.colourLabelBg")}
                      value={ve?.labelBgColor}
                      // Default is transparent (no value set), shown
                      // as a checkered swatch so the user understands
                      // it's "no fill" rather than white.
                      defaultPreview="transparent"
                      shape="square"
                      onChange={(c) => {
                        if (!workspaceId || !branchId) return;
                        ws.setRelationLabelBgColorOnCanvas(
                          workspaceId,
                          branchId,
                          relation.id,
                          c ?? null,
                        );
                      }}
                    />
                  </div>
                );
              })()}
            </section>

            <section>
              <h3 className="mb-1 font-semibold text-(--ld-text) text-xs">
                {t("attributePanel.sectionEndpoints")}
              </h3>
              {(() => {
                const ws = useWorkspaceData.getState();
                const src = ws.store.getConcept(relation.sourceConceptId);
                const tgt = ws.store.getConcept(relation.targetConceptId);
                const srcName = src
                  ? resolveBilingual(src.name, lang) || relation.sourceConceptId
                  : relation.sourceConceptId;
                const tgtName = tgt
                  ? resolveBilingual(tgt.name, lang) || relation.targetConceptId
                  : relation.targetConceptId;
                return (
                  <p className="text-(--ld-muted) text-xs leading-relaxed">
                    <span className="text-(--ld-text)">{t("attributePanel.endpointFrom")}:</span>{" "}
                    <span className={src ? "text-(--ld-text)" : "font-mono text-xs"}>
                      {srcName}
                    </span>
                    <br />
                    <span className="text-(--ld-text)">{t("attributePanel.endpointTo")}:</span>{" "}
                    <span className={tgt ? "text-(--ld-text)" : "font-mono text-xs"}>
                      {tgtName}
                    </span>
                  </p>
                );
              })()}
            </section>

            <section>
              <h3 className="mb-1 font-semibold text-(--ld-text) text-xs">
                {t("attributePanel.sectionOwner")}
              </h3>
              <p className="font-mono text-(--ld-text) text-xs">{relation.ownerEmail}</p>
            </section>

            <section className="text-(--ld-muted) text-xs">
              <p>{t("attributePanel.versionLabel", { n: relation.version })}</p>
            </section>

            <DebugTechnicalSection kind="relation" entityId={relation.id} />
          </div>
        ) : concept ? (
          <div className="flex flex-col gap-3">
            <section>
              <h3 className="mb-1 font-semibold text-(--ld-text) text-xs">
                {t("attributePanel.sectionType")}
              </h3>
              {/* Dit blok toont ALTIJD de v4-werkelijkheid, ook wanneer de
                  weergaveschakelaar op 3.1 staat: het generieke type, het
                  compat-stereotype dat de 3.1-herkomst draagt, en de echte
                  typeId.

                  Waarom niet de weergavemodus volgen (Gerald, 2026-08-09):
                  het attributenpaneel is waar je het MODEL bewerkt, niet waar
                  je een diagram leest. De vermomming daar doortrekken leverde
                  een paneel op dat "Business Process (archimate.Process)" zei
                  — woorden en code die iets anders beweren. Zodra je iets
                  uitzoekt (een export, een overlay-regel, een zoekopdracht op
                  type) heb je juist de echte waarde nodig.

                  De schakelaar gaat dus over hoe je DIAGRAMMEN leest: labels,
                  kleuren, iconen op het canvas en wat het palette aanbiedt. */}
              {(() => {
                const { label, badge } = typeLabelFor(concept, lang, "v4");
                return (
                  <p className="font-mono text-(--ld-text) text-xs">
                    {label}
                    {badge && <span className="ml-1 text-(--ld-muted)">{badge}</span>}
                    <span className="ml-1 text-(--ld-muted)">({concept.typeId})</span>
                  </p>
                );
              })()}
            </section>

            {/*
              Kernel-Name — alleen tonen wanneer GEEN actief stereotype
              een virtueel `storage_mode: "concept_name"`-veld declareert
              (Risk `«Risk».risk_label`, etc.). Die stereotype-velden
              lezen/schrijven dezelfde slot, dus dubbele rendering zou de
              modelleur verwarren.
            */}
            {(() => {
              const hasVirtualPrimaryName = concept.stereotypes.some((stId) => {
                const st = reg.getStereotype(stId);
                if (!st) return false;
                const attrs = reg.resolveStereotypeAttributes(st);
                return attrs.some((a) => a.storage_mode === "concept_name");
              });
              if (hasVirtualPrimaryName) return null;

              // Fase 2 Sharp-glossary: een sharp.Attribute / sharp.Identifier
              // heeft een single-language naam (technische vaste waarde). Met
              // een attribuuttype-referentie is de naam afgeleid (read-only) en
              // volgt 'ie live het gerefereerde sharp.AttributeType.
              const isSharpAttrOrId =
                concept.typeId === "sharp.Attribute" || concept.typeId === "sharp.Identifier";
              if (isSharpAttrOrId) {
                const refId =
                  typeof concept.attributes.attribute_type_ref === "string"
                    ? concept.attributes.attribute_type_ref
                    : "";
                const refTarget = refId
                  ? useWorkspaceData.getState().store.getConcept(refId)
                  : null;
                const derived = !!(refId && refTarget);
                const nameValue = derived
                  ? resolveBilingual(refTarget.name, lang)
                  : (concept.name.nl ?? concept.name.en ?? "");
                return (
                  <section>
                    <label className="flex flex-col gap-1">
                      <span className="font-medium text-(--ld-muted) text-xs">
                        {t("attributePanel.nameNl")}
                      </span>
                      <input
                        className="rounded border border-(--ld-muted) px-2 py-1 text-sm focus:border-(--ld-accent) focus:outline-none disabled:bg-(--ld-raised) disabled:text-(--ld-muted)"
                        value={nameValue}
                        disabled={derived || !stewardEditable}
                        title={derived ? t("attributePanel.nameDerivedHint") : undefined}
                        onChange={(e) => {
                          if (!workspaceId || !branchId) return;
                          const v = e.target.value;
                          void useWorkspaceData
                            .getState()
                            .patchConcept(workspaceId, branchId, concept.id, {
                              name: { nl: v, en: v },
                            });
                        }}
                        onBlur={() => {
                          if (!workspaceId || !branchId) return;
                          void useWorkspaceData
                            .getState()
                            .checkConceptDuplicate(workspaceId, branchId, concept.id);
                        }}
                      />
                    </label>
                    {derived ? (
                      <p className="mt-1 text-xs text-(--ld-muted)">
                        {t("attributePanel.nameDerivedHint")}
                      </p>
                    ) : null}
                  </section>
                );
              }

              return (
                <section>
                  <BilingualField
                    kind="name"
                    labelNl={t("attributePanel.nameNl")}
                    labelEn={t("attributePanel.nameEn")}
                    valueNl={concept.name.nl ?? ""}
                    valueEn={concept.name.en ?? ""}
                    disabled={!stewardEditable}
                    onChange={(lang, value) => {
                      if (!workspaceId || !branchId) return;
                      void useWorkspaceData
                        .getState()
                        .patchConcept(workspaceId, branchId, concept.id, {
                          name: { ...concept.name, [lang]: value },
                        });
                    }}
                    onCommit={() => {
                      // Duplicaat-check pas op COMMIT (blur), niet per toets.
                      if (!workspaceId || !branchId) return;
                      void useWorkspaceData
                        .getState()
                        .checkConceptDuplicate(workspaceId, branchId, concept.id);
                    }}
                  />
                </section>
              );
            })()}

            {/*
              Kernel-Beschrijving — alleen tonen wanneer GEEN actief
              stereotype een virtueel `storage_mode: "concept_description"`-
              veld declareert (Sharp definition_text, C4 description_text).
              Die stereotype-velden lezen/schrijven dezelfde slot, dus
              dubbele rendering zou de modelleur verwarren.
            */}
            {(() => {
              const hasVirtualPrimaryDescription = concept.stereotypes.some((stId) => {
                const st = reg.getStereotype(stId);
                if (!st) return false;
                const attrs = reg.resolveStereotypeAttributes(st);
                return attrs.some((a) => a.storage_mode === "concept_description");
              });
              if (hasVirtualPrimaryDescription) return null;

              // Fase 2 Sharp-glossary: bij een sharp.Attribute/Identifier met
              // een attribuuttype-referentie wordt de beschrijving LIVE afgeleid
              // van de definitie van het gerefereerde sharp.AttributeType
              // (read-only). Zonder referentie blijft 't een gewoon bewerkbaar
              // beschrijvingsveld.
              const isSharpAttrOrIdDesc =
                concept.typeId === "sharp.Attribute" || concept.typeId === "sharp.Identifier";
              if (isSharpAttrOrIdDesc) {
                const refId =
                  typeof concept.attributes.attribute_type_ref === "string"
                    ? concept.attributes.attribute_type_ref
                    : "";
                const refTarget = refId
                  ? useWorkspaceData.getState().store.getConcept(refId)
                  : null;
                if (refId && refTarget) {
                  const def = refTarget.attributes.definition;
                  const defText =
                    def && typeof def === "object"
                      ? resolveBilingual(def as Record<string, string>, lang)
                      : "";
                  return (
                    <section>
                      <label className="flex flex-col gap-1">
                        <span className="font-medium text-(--ld-muted) text-xs">
                          {t("attributePanel.descriptionNl")}
                        </span>
                        <textarea
                          className="min-h-[3rem] rounded border border-(--ld-muted) px-2 py-1 text-sm disabled:bg-(--ld-raised) disabled:text-(--ld-muted)"
                          value={defText}
                          disabled
                          title={t("attributePanel.descriptionDerivedHint")}
                        />
                      </label>
                      <p className="mt-1 text-xs text-(--ld-muted)">
                        {t("attributePanel.descriptionDerivedHint")}
                      </p>
                    </section>
                  );
                }
              }

              return (
                <section>
                  <BilingualField
                    kind="description"
                    labelNl={t("attributePanel.descriptionNl")}
                    labelEn={t("attributePanel.descriptionEn")}
                    valueNl={concept.description.nl ?? ""}
                    valueEn={concept.description.en ?? ""}
                    multiline
                    onChange={(lang, value) => {
                      if (!workspaceId || !branchId) return;
                      void useWorkspaceData
                        .getState()
                        .patchConcept(workspaceId, branchId, concept.id, {
                          description: { ...concept.description, [lang]: value },
                        });
                    }}
                  />
                </section>
              );
            })()}

            {/* Alt-labels per view-resolver (M8 §5) — alleen wanneer dit
                concept-type op ≥1 viewpoint met een label_resolver valt. */}
            {(() => {
              const resolvers = relevantResolversForType(concept.typeId);
              if (resolvers.length === 0) return null;
              return (
                <AltLabelsSection
                  key={concept.id}
                  resolvers={resolvers}
                  altLabels={concept.altLabels}
                  disabled={!stewardEditable}
                  onChange={(resolver, lang, value) => {
                    if (!workspaceId || !branchId) return;
                    const current = concept.altLabels[resolver] ?? {};
                    void useWorkspaceData
                      .getState()
                      .patchConcept(workspaceId, branchId, concept.id, {
                        alt_labels: { [resolver]: { ...current, [lang]: value } },
                      });
                  }}
                />
              );
            })()}

            {/* ── ArchiMate-notation-keuze (M6.5-W9). Voor archimate-base-
                 types zonder stereotype: kies tussen rechthoek (default)
                 of het ArchiMate-3.2-symbool. Stereotypes claimen de
                 vorm al (bv. risk.Vulnerability = octagon), dus de keuze
                 verbergen we zodra er een stereotype actief is. */}
            {(() => {
              if (!concept.typeId.startsWith("archimate.")) return null;
              // Stereotypen toelaten zolang er een SVG-icoon bestaat
              // (risk-Risk / sharp-Concept / c4-Container etc. hebben
              // hun eigen SVG en kunnen dus in archimate-symbool-modus).
              if (
                concept.stereotypes.length > 0 &&
                !hasArchimateSvgIcon(concept.typeId, concept.stereotypes)
              )
                return null;
              const current =
                (concept.attributes.notation_style as string | undefined) ?? "rectangle";
              const setNotation = (next: "rectangle" | "archimate") => {
                if (!workspaceId || !branchId) return;
                // Resize EERST zodat het in de viewState staat vóór
                // patchConcept een eventuele diagram-copy-on-write
                // cascade triggert — anders kopieert de cascade de oude
                // (grote) viewState en springt de lijn na de PATCH terug.
                if (next === "archimate") {
                  const shape = getArchimateShape(concept.typeId);
                  // Auto-resize bij iconic-shapes EN bij elke shape die
                  // een SVG-icoon heeft (Process / Function / Event /
                  // etc. krijgen nu ook hun natuurlijke SVG-vorm).
                  const useSvg = hasArchimateSvgIcon(concept.typeId, concept.stereotypes);
                  if (shape && (shape.iconic || useSvg)) {
                    const state = useWorkspaceData.getState();
                    if (state.activeDiagramId) {
                      // Fallback 80×64 voor shapes zonder eigen
                      // defaultSize (typisch clip-path-shapes).
                      const size = shape.defaultSize ?? { width: 80, height: 64 };
                      state.resizeNodeOnCanvas(workspaceId, branchId, concept.id, size);
                      // Default label-positie buiten + onder de figuur —
                      // standaard ArchiMate-conventie. Gebruiker kan
                      // later handmatig aanpassen via toolbar.
                      state.setNodeStyleOnCanvas(workspaceId, branchId, concept.id, {
                        labelAlignY: "bottom",
                        labelOutside: true,
                      });
                    }
                  }
                }
                void useWorkspaceData.getState().patchConcept(workspaceId, branchId, concept.id, {
                  attributes: { ...concept.attributes, notation_style: next },
                });
              };
              return (
                <section className="flex flex-col gap-1">
                  <h3 className="font-semibold text-(--ld-text) text-xs">
                    {t("attributePanel.sectionNotation", "Notatie")}
                  </h3>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      className={`flex-1 rounded border px-2 py-1 text-xs ${
                        current === "rectangle"
                          ? "border-(--ld-accent) bg-(--ld-active) font-semibold text-(--ld-accent)"
                          : "border-(--ld-control) bg-(--ld-surface) text-(--ld-muted) hover:bg-(--ld-raised)"
                      }`}
                      onClick={() => setNotation("rectangle")}
                    >
                      {t("attributePanel.notationRectangle", "Rechthoek")}
                    </button>
                    <button
                      type="button"
                      className={`flex-1 rounded border px-2 py-1 text-xs ${
                        current === "archimate"
                          ? "border-(--ld-accent) bg-(--ld-active) font-semibold text-(--ld-accent)"
                          : "border-(--ld-control) bg-(--ld-surface) text-(--ld-muted) hover:bg-(--ld-raised)"
                      }`}
                      onClick={() => setNotation("archimate")}
                    >
                      {t("attributePanel.notationArchimate", "ArchiMate-symbool")}
                    </button>
                  </div>
                </section>
              );
            })()}

            {/* ── Stereotypes ──────────────────────────────────────── */}
            {(() => {
              // Huidige stereotypes meegeven zodat conditionele
              // `applies_to`-entries (`requires_stereotype`) meetellen —
              // bv. «Customer Relationship» verschijnt pas op een Process
              // die óók «archimate31.BusinessProcess» draagt.
              // Compat-stereotypes horen NIET in deze lijst. Ze zijn geen
              // overlay die je aan- of uitzet, maar de 3.1-identiteit van het
              // concept zelf — die staat al in het TYPE-blok hierboven. Als
              // vinkje zou de modelleur 'm kunnen uitzetten (identiteit weg,
              // kleur weg, AMEFF-export gokt) of er twee tegelijk aanzetten
              // (een proces dat zowel Business- als Application Process is).
              // Hertyperen doe je via de type-wijzig-dialoog, niet hier.
              const available = reg
                .stereotypesFor(concept.typeId, concept.stereotypes ?? [])
                .filter((s) => !isCompatStereotype(s.id));
              if (available.length === 0) return null;
              // Sorteer alfabetisch op de getoonde label (bilingual-resolved).
              // Zonder dit komt de volgorde uit registry-pack-load-volgorde
              // en is voor de modelleur onvoorspelbaar.
              const availableSorted = [...available].sort((a, b) =>
                resolveBilingual(a.label, lang).localeCompare(
                  resolveBilingual(b.label, lang),
                  lang,
                ),
              );
              const active = new Set(concept.stereotypes);
              const toggle = (stereotypeId: string) => {
                if (!workspaceId || !branchId) return;
                const next = active.has(stereotypeId)
                  ? concept.stereotypes.filter((s) => s !== stereotypeId)
                  : [...concept.stereotypes, stereotypeId];
                void useWorkspaceData
                  .getState()
                  .patchConcept(workspaceId, branchId, concept.id, { stereotypes: next });
              };
              return (
                <section className="flex flex-col gap-1">
                  <h3 className="font-semibold text-(--ld-text) text-xs">
                    {t("attributePanel.sectionStereotypes")}
                  </h3>
                  <div className="flex flex-col gap-0.5">
                    {availableSorted.map((st) => {
                      const checked = active.has(st.id);
                      return (
                        <label
                          key={st.id}
                          className={`flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-xs hover:bg-(--ld-raised) ${
                            checked ? "text-(--ld-text)" : "text-(--ld-muted)"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggle(st.id)}
                            className="h-3 w-3"
                          />
                          <span className={checked ? "font-semibold" : ""}>
                            {resolveBilingual(st.label, lang)}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </section>
              );
            })()}

            {/* ── Base-type attributen (na DOM-cleanup vrijwel altijd
                 leeg, behouden voor backward-compat met legacy-records).
                 Voor drawing.Rectangle filteren we link_kind/link_target
                 uit de standaard-render — die krijgen hieronder een eigen
                 adaptieve sectie. */}
            {elementType?.attributes &&
              elementType.attributes.filter(
                (s) =>
                  !(
                    concept.typeId === "drawing.Rectangle" &&
                    (s.name === "link_kind" || s.name === "link_target")
                  ),
              ).length > 0 && (
                <section className="flex flex-col gap-2">
                  <h3 className="font-semibold text-(--ld-text) text-xs">
                    {t("attributePanel.sectionAttributes")}
                  </h3>
                  {elementType.attributes
                    .filter(
                      (s) =>
                        !(
                          concept.typeId === "drawing.Rectangle" &&
                          (s.name === "link_kind" || s.name === "link_target")
                        ),
                    )
                    .map((schema) => (
                      <AttributeField
                        key={schema.name}
                        schema={schema}
                        value={concept.attributes[schema.name]}
                        disabled={schema.owner_only && !stewardEditable}
                        lang={lang}
                        onChange={(value) => {
                          if (!workspaceId || !branchId) return;
                          void useWorkspaceData
                            .getState()
                            .patchConcept(workspaceId, branchId, concept.id, {
                              attributes: { ...concept.attributes, [schema.name]: value },
                            });
                        }}
                      />
                    ))}
                </section>
              )}

            {/* ── Adaptive link-sectie voor drawing.Rectangle. Dropdown
                 (URL/Diagram/Geen) plus een tweede veld dat zich aanpast:
                 URL-input of diagram-selector. Linkermuisklik op de
                 rechthoek op het canvas navigeert dan naar de bestemming. */}
            {concept.typeId === "drawing.Rectangle" && workspaceId && branchId && (
              <DrawingRectangleLinkSection
                concept={concept}
                workspaceId={workspaceId}
                branchId={branchId}
                lang={lang}
              />
            )}

            {/* ── CWE-bibliotheek-picker: alleen voor «Vulnerability»-
                 concepten. Zoek-en-kopieer i.p.v. materialiseren — zie
                 CLAUDE.md 2026-07-24. */}
            {concept.stereotypes.includes("risk.Vulnerability") && workspaceId && branchId && (
              <CweLibraryPicker
                concept={concept}
                workspaceId={workspaceId}
                branchId={branchId}
                lang={lang}
              />
            )}

            {/* ── Stereotype-attributen: per actief stereotype een
                 attribuut-bundle. Bij ≥2 bundles komen ze in tabs
                 (M8), bij 1 als enkele sectie. */}
            <StereotypeBundleTabs
              concept={concept}
              workspaceId={workspaceId}
              branchId={branchId}
              lang={lang}
              stewardEditable={stewardEditable}
            />

            <section>
              <h3 className="mb-1 font-semibold text-(--ld-text) text-xs">
                {t("attributePanel.sectionColours")}
              </h3>
              {(() => {
                const ws = useWorkspaceData.getState();
                const dId = ws.activeDiagramId;
                const ve = dId ? ws.store.getDiagram(dId)?.viewState[concept.id] : null;
                const ns = ve?.nodeStyle ?? {};
                // Allow each style field to be EXPLICITLY set to undefined
                // (= clear the override and fall back to layer-derived).
                // Plain `Partial<NodeStyle>` doesn't permit undefined under
                // strict exactOptionalPropertyTypes.
                type NodeStylePatch = {
                  [K in keyof NodeStyle]?: NodeStyle[K] | undefined;
                };
                const setStyle = (patch: NodeStylePatch) => {
                  if (!workspaceId || !branchId) return;
                  ws.setNodeStyleOnCanvas(
                    workspaceId,
                    branchId,
                    concept.id,
                    patch as Partial<NodeStyle>,
                  );
                };
                return (
                  <div className="flex flex-wrap items-center gap-3">
                    <NodeColorSlot
                      label={t("attributePanel.colourFill")}
                      value={ns.fillColor}
                      defaultPreview="#FFF6B0"
                      shape="square"
                      onChange={(c) => setStyle({ fillColor: c })}
                    />
                    <NodeColorSlot
                      label={t("attributePanel.colourStroke")}
                      value={ns.strokeColor}
                      defaultPreview="#94A3B8"
                      shape="border"
                      // Toelaten dat gebruiker stroke expliciet op
                      // transparant zet — randloze rechthoek = vrij
                      // tekstblok-effect, zonder nieuw drawing-type.
                      allowTransparent
                      onChange={(c) => setStyle({ strokeColor: c })}
                    />
                    <NodeColorSlot
                      label={t("attributePanel.colourText")}
                      value={ns.fontColor}
                      defaultPreview="#0F172A"
                      shape="A"
                      onChange={(c) => setStyle({ fontColor: c })}
                    />
                  </div>
                );
              })()}
            </section>

            <section>
              <h3 className="mb-1 font-semibold text-(--ld-text) text-xs">
                {t("attributePanel.sectionOwner")}
              </h3>
              <p className="font-mono text-(--ld-text) text-xs">{concept.ownerEmail}</p>
              {!stewardEditable && (
                <p className="mt-1 text-(--ld-muted) text-xs">
                  {t("attributePanel.readonlyOwnerHint")}
                </p>
              )}
            </section>

            <section className="text-(--ld-muted) text-xs">
              <p>{t("attributePanel.versionLabel", { n: concept.version })}</p>
            </section>

            <DebugTechnicalSection kind="concept" entityId={concept.id} />
          </div>
        ) : diagram ? (
          <DiagramForm diagram={diagram} lang={lang} />
        ) : null}
      </fieldset>
      <ResizeHandle />
    </aside>
  );
}

/**
 * Diagram-modus van de AttributePanel — getoond wanneer de gebruiker
 * vanuit de M6 Explorer een diagram bewerkt. Hergebruikt `BilingualField`
 * voor naam + beschrijving en biedt een viewKind-picker plus read-only
 * branch/id-info.
 */
function DiagramForm({ diagram, lang }: { diagram: Diagram; lang: string }) {
  const { t } = useTranslation();
  const workspaceId = useSession((s) => s.workspaceId);
  const branchId = useSession((s) => s.branchId);
  const reg = getRegistry();
  const viewKinds = reg.listActiveViewKinds();
  const currentVk = reg.getViewKind(diagram.viewKind);
  return (
    <div className="flex flex-col gap-3">
      <section>
        <h3 className="mb-1 font-semibold text-(--ld-text) text-xs">
          {t("attributePanel.sectionType")}
        </h3>
        <select
          className="w-full rounded border border-(--ld-muted) bg-(--ld-surface) px-2 py-1 text-sm focus:border-(--ld-accent) focus:outline-none"
          value={diagram.viewKind}
          onChange={(e) => {
            if (!workspaceId || !branchId) return;
            void useWorkspaceData
              .getState()
              .changeDiagramViewKind(workspaceId, branchId, diagram.id, e.target.value);
          }}
        >
          {!currentVk && (
            <option value={diagram.viewKind}>
              {t("attributePanel.unknownViewKind", { viewKind: diagram.viewKind })}
            </option>
          )}
          {viewKinds.map((vk) => (
            <option key={vk.id} value={vk.id}>
              {resolveBilingual(vk.name, lang)}
              {vk.custom ? " ★" : ""}
            </option>
          ))}
        </select>
      </section>

      <section>
        <BilingualField
          kind="name"
          labelNl={t("attributePanel.nameNl")}
          labelEn={t("attributePanel.nameEn")}
          valueNl={diagram.name.nl ?? ""}
          valueEn={diagram.name.en ?? ""}
          onChange={(l, value) => {
            if (!workspaceId || !branchId) return;
            void useWorkspaceData.getState().renameDiagram(workspaceId, branchId, diagram.id, {
              ...diagram.name,
              [l]: value,
            });
          }}
        />
      </section>

      <section>
        <BilingualField
          kind="description"
          labelNl={t("attributePanel.descriptionNl")}
          labelEn={t("attributePanel.descriptionEn")}
          valueNl={diagram.description?.nl ?? ""}
          valueEn={diagram.description?.en ?? ""}
          multiline
          onChange={(l, value) => {
            if (!workspaceId || !branchId) return;
            void useWorkspaceData
              .getState()
              .setDiagramDescription(workspaceId, branchId, diagram.id, {
                ...(diagram.description ?? {}),
                [l]: value,
              });
          }}
        />
      </section>

      {currentVk?.attributes && currentVk.attributes.length > 0 && workspaceId && branchId && (
        <ViewKindAttributesSection
          diagram={diagram}
          viewKind={currentVk}
          workspaceId={workspaceId}
          branchId={branchId}
          lang={lang}
        />
      )}
    </div>
  );
}

/**
 * Diagram-niveau attribuut-sectie — gerendered wanneer de view-kind
 * een `attributes`-blok declareert. Eerste use-case:
 * `analytics.heat-map` (target_type, vertical/horizontal_attribute,
 * color_start, color_end, apply_color, dot_fill_color, dot_text_color).
 * De drie heat-map-enums (target_type + twee assen) hebben dynamische
 * opties — die worden runtime berekend uit de registry.
 */
function ViewKindAttributesSection({
  diagram,
  viewKind,
  workspaceId,
  branchId,
  lang,
}: {
  diagram: Diagram;
  viewKind: import("@ae/domain").ViewKind;
  workspaceId: string;
  branchId: string;
  lang: string;
}) {
  const { t } = useTranslation();
  const reg = getRegistry();
  const isHeatMap = viewKind.layout_mode === "heat-map" && viewKind.id === "analytics.heat-map";
  const isRelationHeatMap = viewKind.id === "analytics.relation-heat-map";

  // Heat-map: resolve target's attribute-schema list for axis-dropdowns.
  const targetKey =
    isHeatMap && typeof diagram.attributes.target_type === "string"
      ? (diagram.attributes.target_type as string)
      : "";
  const axisCandidates = useMemo(() => {
    if (!isHeatMap || !targetKey) return [] as { name: string; label: string }[];
    let schemas: import("@ae/domain").AttributeSchema[] = [];
    if (targetKey.startsWith("stereotype:")) {
      const st = reg.getStereotype(targetKey.slice("stereotype:".length));
      schemas = [...(st?.attributes ?? [])];
      const applies = st ? reg.appliesToTypeIds(st) : [];
      const baseId = applies.find((id) => id !== "*");
      if (baseId) {
        const baseType = reg.getElementType(baseId);
        if (baseType?.attributes) schemas.push(...baseType.attributes);
      }
    } else if (targetKey.startsWith("type:")) {
      const et = reg.getElementType(targetKey.slice("type:".length));
      schemas = [...(et?.attributes ?? [])];
    }
    return schemas
      .filter((a) => {
        const baseType = a.type.endsWith("?") ? a.type.slice(0, -1) : a.type;
        return baseType === "enum" || baseType === "int";
      })
      .map((a) => ({ name: a.name, label: resolveBilingual(a.label, lang) }));
  }, [isHeatMap, targetKey, reg, lang]);

  // Relation-heatmap: dynamische opties voor 4 stereotype/attribuut-dropdowns.
  const relStereoCurrent =
    isRelationHeatMap && typeof diagram.attributes.relation_stereotype === "string"
      ? (diagram.attributes.relation_stereotype as string)
      : "";
  const tmplStereoCurrent =
    isRelationHeatMap && typeof diagram.attributes.template_stereotype === "string"
      ? (diagram.attributes.template_stereotype as string)
      : "";
  const relationStereotypeOptions = useMemo(() => {
    if (!isRelationHeatMap) return null;
    return reg
      .listStereotypes()
      .filter((s) => {
        // Relatie-stereotypes: applies_to bevat een relation-type-id.
        const applies = reg.appliesToTypeIds(s);
        return applies.some((id) => reg.getRelationType(id) !== undefined);
      })
      .sort((a, b) =>
        resolveBilingual(a.label, lang).localeCompare(resolveBilingual(b.label, lang)),
      )
      .map((s) => ({ value: s.id, label: `«${resolveBilingual(s.label, lang)}»` }));
  }, [isRelationHeatMap, reg, lang]);
  const templateStereotypeOptions = useMemo(() => {
    if (!isRelationHeatMap) return null;
    // Template mag een concept-stereotype ÓF een gewoon ArchiMate-type
    // zijn. We bieden beide aan als bare id's (de canvas probeert eerst
    // getStereotype, dan getElementType).
    const stereos = reg
      .listStereotypes()
      .filter((s) => reg.appliesToTypeIds(s).some((id) => reg.getElementType(id) !== undefined))
      .sort((a, b) =>
        resolveBilingual(a.label, lang).localeCompare(resolveBilingual(b.label, lang)),
      )
      .map((s) => ({ value: s.id, label: `«${resolveBilingual(s.label, lang)}»` }));
    const types = reg
      .listActiveElementTypes()
      .slice()
      .sort((a, b) =>
        resolveBilingual(a.label, lang).localeCompare(resolveBilingual(b.label, lang)),
      )
      .map((t) => ({ value: t.id, label: resolveBilingual(t.label, lang) }));
    return [...stereos, ...types];
  }, [isRelationHeatMap, reg, lang]);
  // Attribuut-opties voor een stereotype (resolveStereotypeAttributes) of
  // een element-type (type.attributes). Alleen enum/int/float als as.
  const buildAttrOptionsForTemplate = (id: string) => {
    if (!id) return [] as { value: string; label: string }[];
    const st = reg.getStereotype(id);
    const schemas = st
      ? reg.resolveStereotypeAttributes(st)
      : (reg.getElementType(id)?.attributes ?? []);
    return schemas
      .filter((a) => {
        const baseType = a.type.endsWith("?") ? a.type.slice(0, -1) : a.type;
        return baseType === "enum" || baseType === "int" || baseType === "float";
      })
      .map((a) => ({ value: a.name, label: resolveBilingual(a.label, lang) }));
  };
  const relationAttributeOptions = useMemo(
    () => (isRelationHeatMap ? buildAttrOptionsForTemplate(relStereoCurrent) : null),
    [isRelationHeatMap, relStereoCurrent, reg, lang],
  );
  const templateAttributeOptions = useMemo(
    () => (isRelationHeatMap ? buildAttrOptionsForTemplate(tmplStereoCurrent) : null),
    [isRelationHeatMap, tmplStereoCurrent, reg, lang],
  );

  // Kleur-velden die als horizontaal paar naast elkaar renderen
  // (eerste veld = key, tweede = waarde). Geldt voor beide heatmaps.
  const HORIZONTAL_PAIRS: Record<string, string> = {
    color_start: "color_end",
    dot_fill_color: "dot_text_color",
    dot_fill_color_instance: "dot_text_color_instance",
  };
  const pairPartners = new Set(Object.values(HORIZONTAL_PAIRS));

  const renderField = (schema: import("@ae/domain").AttributeSchema) => (
    <DiagramAttrField
      key={schema.name}
      schema={schema}
      value={diagram.attributes[schema.name]}
      lang={lang}
      heatMapTargetOptions={
        isHeatMap && schema.name === "target_type" ? buildHeatMapTargetOptions(reg, lang) : null
      }
      heatMapAxisOptions={
        isHeatMap &&
        (schema.name === "vertical_attribute" || schema.name === "horizontal_attribute")
          ? axisCandidates
          : null
      }
      dynamicOptions={
        isRelationHeatMap
          ? schema.name === "relation_stereotype"
            ? relationStereotypeOptions
            : schema.name === "template_stereotype"
              ? templateStereotypeOptions
              : schema.name === "relation_attribute"
                ? relationAttributeOptions
                : schema.name === "template_attribute"
                  ? templateAttributeOptions
                  : null
          : null
      }
      onChange={(next) => {
        useWorkspaceData
          .getState()
          .setDiagramAttribute(workspaceId, branchId, diagram.id, schema.name, next);
        // Bij wisselen van target_type: reset assen, want oude
        // attribuut-namen kunnen op het nieuwe target niet bestaan.
        if (isHeatMap && schema.name === "target_type") {
          useWorkspaceData
            .getState()
            .setDiagramAttribute(workspaceId, branchId, diagram.id, "vertical_attribute", null);
          useWorkspaceData
            .getState()
            .setDiagramAttribute(workspaceId, branchId, diagram.id, "horizontal_attribute", null);
        }
      }}
    />
  );

  // Instance-bolletje-kleuren zijn alleen relevant op niveau Instantie.
  const instanceLevelActive = diagram.attributes.data_level === "instance";
  const instanceOnlyFields = new Set(["dot_fill_color_instance", "dot_text_color_instance"]);

  const allSchemas = (viewKind.attributes ?? []).filter(
    (s) => instanceLevelActive || !instanceOnlyFields.has(s.name),
  );
  const byName = new Map(allSchemas.map((s) => [s.name, s]));

  return (
    <section>
      <h3 className="mb-1 font-semibold text-(--ld-text) text-xs">
        {t("attributePanel.configuration")}
      </h3>
      <div className="flex flex-col gap-2">
        {allSchemas.map((schema) => {
          // Tweede helft van een paar: al gerenderd door de eerste helft.
          if (pairPartners.has(schema.name)) return null;
          const partnerName = HORIZONTAL_PAIRS[schema.name];
          const partner = partnerName ? byName.get(partnerName) : undefined;
          if (partner) {
            return (
              <div key={schema.name} className="flex items-start gap-2">
                <div className="min-w-0 flex-1">{renderField(schema)}</div>
                <div className="min-w-0 flex-1">{renderField(partner)}</div>
              </div>
            );
          }
          return renderField(schema);
        })}
      </div>
    </section>
  );
}

function buildHeatMapTargetOptions(
  reg: import("@ae/domain").RegistryRuntime,
  lang: string,
): { value: string; label: string; group: "type" | "stereotype" }[] {
  const out: { value: string; label: string; group: "type" | "stereotype" }[] = [];
  for (const st of reg.listStereotypes()) {
    out.push({
      value: `stereotype:${st.id}`,
      label: `«${resolveBilingual(st.label, lang)}»`,
      group: "stereotype",
    });
  }
  for (const et of reg.listActiveElementTypes()) {
    out.push({
      value: `type:${et.id}`,
      label: resolveBilingual(et.label, lang),
      group: "type",
    });
  }
  out.sort((a, b) => a.label.localeCompare(b.label));
  return out;
}

function DiagramAttrField({
  schema,
  value,
  lang,
  heatMapTargetOptions,
  heatMapAxisOptions,
  dynamicOptions,
  onChange,
}: {
  schema: import("@ae/domain").AttributeSchema;
  value: unknown;
  lang: string;
  heatMapTargetOptions: { value: string; label: string; group: "type" | "stereotype" }[] | null;
  heatMapAxisOptions: { name: string; label: string }[] | null;
  /** Generieke runtime-opties die schema.options overrulen wanneer
   *  gezet — gebruikt door relation-heatmap voor de stereotype- en
   *  attribuut-dropdowns die afhankelijk van workspace-data runtime
   *  worden gevuld. */
  dynamicOptions?: { value: string; label: string }[] | null;
  onChange: (next: unknown) => void;
}) {
  const { t } = useTranslation();
  const baseType = schema.type.endsWith("?") ? schema.type.slice(0, -1) : schema.type;
  const required = !schema.type.endsWith("?");
  const label = resolveBilingual(schema.label, lang);
  const current = typeof value === "string" ? value : "";

  if (baseType === "color") {
    return (
      <NodeColorSlot
        label={`${label}${required ? " *" : ""}`}
        value={typeof value === "string" && value ? value : undefined}
        defaultPreview="#e5e7eb"
        shape="square"
        onChange={(next) => onChange(next ?? null)}
      />
    );
  }

  if (baseType === "bool") {
    // Boolean als Ja/Nee-dropdown. Ongezet (undefined) telt als Ja
    // (default-aan-semantiek: de canvas leest `attr !== false`).
    const boolVal = value === false ? "false" : "true";
    return (
      <label className="flex flex-col gap-0.5">
        <span className="text-(--ld-muted) text-xs">
          {label}
          {required && <span className="text-(--ld-negative)"> *</span>}
        </span>
        <select
          className="w-full rounded border border-(--ld-muted) bg-(--ld-surface) px-2 py-1 text-xs focus:border-(--ld-accent) focus:outline-none"
          value={boolVal}
          onChange={(e) => onChange(e.target.value === "true")}
        >
          <option value="true">{t("attributePanel.yes")}</option>
          <option value="false">{t("attributePanel.no")}</option>
        </select>
        {schema.description && (
          <span className="text-xs italic text-(--ld-muted)">
            {resolveBilingual(schema.description, lang)}
          </span>
        )}
      </label>
    );
  }

  if (baseType === "enum") {
    let options: { value: string; label: string }[] = [];
    if (heatMapTargetOptions) {
      // Group stereotypes and types under <optgroup>s — handled in render.
      options = heatMapTargetOptions.map((o) => ({ value: o.value, label: o.label }));
    } else if (heatMapAxisOptions) {
      options = heatMapAxisOptions.map((a) => ({ value: a.name, label: a.label }));
    } else if (dynamicOptions) {
      options = dynamicOptions;
    } else if (schema.options) {
      options = schema.options.map((opt) => ({
        value: opt,
        label:
          schema.option_labels?.[opt] !== undefined
            ? resolveBilingual(schema.option_labels[opt]!, lang)
            : opt,
      }));
    }
    return (
      <label className="flex flex-col gap-0.5">
        <span className="text-(--ld-muted) text-xs">
          {label}
          {required && <span className="text-(--ld-negative)"> *</span>}
        </span>
        <select
          className="w-full rounded border border-(--ld-muted) bg-(--ld-surface) px-2 py-1 text-xs focus:border-(--ld-accent) focus:outline-none"
          value={current}
          onChange={(e) => onChange(e.target.value || null)}
        >
          <option value="">{t("attributePanel.chooseOption")}</option>
          {heatMapTargetOptions ? (
            <>
              <optgroup label={t("attributePanel.sectionStereotypes")}>
                {heatMapTargetOptions
                  .filter((o) => o.group === "stereotype")
                  .map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
              </optgroup>
              <optgroup label={t("attributePanel.optgroupTypes")}>
                {heatMapTargetOptions
                  .filter((o) => o.group === "type")
                  .map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
              </optgroup>
            </>
          ) : (
            options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))
          )}
        </select>
        {schema.description && (
          <span className="text-xs italic text-(--ld-muted)">
            {resolveBilingual(schema.description, lang)}
          </span>
        )}
      </label>
    );
  }

  // Fallback: free-text string for any unrecognized type.
  return (
    <label className="flex flex-col gap-0.5">
      <span className="text-(--ld-muted) text-xs">{label}</span>
      <input
        type="text"
        className="w-full rounded border border-(--ld-muted) bg-(--ld-surface) px-2 py-1 text-xs focus:border-(--ld-accent) focus:outline-none"
        value={current}
        onChange={(e) => onChange(e.target.value || null)}
      />
    </label>
  );
}

/**
 * Compact colour-swatch slot used in the AttributePanel. Shows the
 * current colour as a small preview that doubles as a click target —
 * clicking it opens the same SwatchColorPicker the toolbar uses, with
 * eyedropper, theme palette, and clear-to-default support.
 *
 * Three preview shapes:
 *  - `square`: filled square (for fill / label-bg colours)
 *  - `border`: hollow square with coloured border (for stroke / line)
 *  - `A`:      letter "A" with a coloured underline (for text colour)
 */
function NodeColorSlot({
  label,
  value,
  defaultPreview,
  shape,
  allowTransparent,
  onChange,
}: {
  label: string;
  value: string | undefined;
  /** Colour used in the preview when `value` is unset (= layer-derived).
   *  Pass `"transparent"` to render a checkered swatch + relabel the
   *  picker's clear-to-default button as "Transparant". */
  defaultPreview: string;
  shape: "square" | "border" | "A" | "line";
  /** Show the explicit "Transparant" button in the picker — for stroke
   *  this lets the user create a borderless drawing-rectangle that
   *  effectively becomes a free text-block. */
  allowTransparent?: boolean;
  onChange: (color: string | undefined) => void;
}) {
  const { t } = useTranslation();
  const previewColor = value ?? defaultPreview;
  const isTransparentDefault = defaultPreview === "transparent";
  return (
    <div className="flex flex-col items-start gap-0.5 text-xs text-(--ld-muted)">
      <span>{label}</span>
      <SwatchColorPicker
        title={t("attributePanel.colourSlotTitle", { label })}
        value={value}
        allowClear
        {...(allowTransparent ? { allowTransparent: true } : {})}
        clearLabel={isTransparentDefault ? t("attributePanel.transparent") : undefined}
        onChange={onChange}
        renderTrigger={({ onClick, open }) => (
          <button
            type="button"
            onClick={onClick}
            className={`flex h-7 w-7 cursor-pointer items-center justify-center rounded border ${open ? "border-(--ld-accent) bg-(--ld-raised)" : "border-(--ld-muted) bg-(--ld-surface)"} hover:bg-(--ld-raised)`}
            title={value ?? t("attributePanel.colourDefault")}
          >
            {shape === "square" && (
              <span
                className="h-4 w-4 rounded border border-(--ld-control)"
                style={
                  previewColor === "transparent"
                    ? {
                        // Checkered pattern signals "no background colour"
                        // (transparent / inherit). Slate-300 contrast keeps
                        // the pattern visible against the white-ish panel
                        // background.
                        backgroundImage:
                          "linear-gradient(45deg, #cbd5e1 25%, transparent 25%, transparent 75%, #cbd5e1 75%), linear-gradient(45deg, #cbd5e1 25%, transparent 25%, transparent 75%, #cbd5e1 75%)",
                        backgroundSize: "6px 6px",
                        backgroundPosition: "0 0, 3px 3px",
                      }
                    : { backgroundColor: previewColor }
                }
              />
            )}
            {shape === "border" && (
              <span
                // Vaste lichte ondergrond, zoals het object op het canvas:
                // ook in de donkere modus toont het vakje de canvaskleur.
                className="h-4 w-4 rounded border-2 bg-white"
                style={{ borderColor: previewColor }}
              />
            )}
            {shape === "A" && (
              <span
                className="relative flex h-5 w-5 items-center justify-center rounded-sm bg-white font-semibold"
                style={{ color: previewColor }}
              >
                A
                <span
                  className="absolute right-0 bottom-0 left-0 h-0.5 rounded"
                  style={{ backgroundColor: previewColor }}
                />
              </span>
            )}
            {shape === "line" && (
              <span className="block h-1 w-4 rounded" style={{ backgroundColor: previewColor }} />
            )}
          </button>
        )}
      />
    </div>
  );
}

function RelationTypePicker({
  relationTypeId,
  sourceConceptId,
  targetConceptId,
  lang,
  onChange,
}: {
  relationTypeId: string;
  sourceConceptId: string;
  targetConceptId: string;
  lang: string;
  /** `reversed=true` means: also swap source/target on the relation. */
  onChange: (newTypeId: string, reversed: boolean) => void;
}) {
  const { t } = useTranslation();
  const reg = getRegistry();
  const store = useWorkspaceData.getState().store;
  const source = store.getConcept(sourceConceptId);
  const target = store.getConcept(targetConceptId);
  // Start from the matrix-valid set, then narrow further with the
  // active diagram's view-kind allowlist (`included_relation_types`).
  // The current relation's existing type is always kept in the list so
  // the user can still see what's selected even if the diagram-type
  // restricts it.
  const matrixForward =
    source && target ? validRelationTypesBetween(source.typeId, target.typeId) : [];
  const matrixReverse =
    source && target ? validRelationTypesReversed(source.typeId, target.typeId) : [];
  const activeDiagramId = useWorkspaceData((s) => s.activeDiagramId);
  const viewAllow = (() => {
    if (!activeDiagramId) return null;
    const d = store.getDiagram(activeDiagramId);
    if (!d) return null;
    const vk = reg.getViewKind(d.viewKind);
    // Tekenrelaties staan boven de view-kind-restrictie, net als de
    // tekenelementen zelf — zie `alwaysAllowedRelationTypeIds`.
    return vk?.included_relation_types && vk.included_relation_types.length > 0
      ? new Set([...vk.included_relation_types, ...alwaysAllowedRelationTypeIds()])
      : null;
  })();
  const applyAllow = (ids: string[]) =>
    viewAllow ? ids.filter((id) => viewAllow.has(id) || id === relationTypeId) : ids;
  const forward = applyAllow(matrixForward);
  const reverse = applyAllow(matrixReverse);

  const labelFor = (id: string) => {
    const rt = reg.getRelationType(id);
    return rt ? resolveBilingual(rt.label, lang) : id;
  };

  // Encode value as "id" for forward, "id|reversed" for reverse, so a single
  // <select> can represent both. The current relation's typeId is always
  // forward (it already exists with this source/target).
  const forwardValue = relationTypeId;
  return (
    <div className="flex flex-col gap-1">
      <select
        className="rounded border border-(--ld-muted) bg-(--ld-surface) px-2 py-1 text-sm focus:border-(--ld-accent) focus:outline-none"
        value={forwardValue}
        onChange={(e) => {
          const raw = e.target.value;
          const reversed = raw.endsWith("|reversed");
          const id = reversed ? raw.replace("|reversed", "") : raw;
          if (!reversed && id === relationTypeId) return;
          onChange(id, reversed);
        }}
      >
        {!forward.includes(relationTypeId) && (
          <option value={relationTypeId}>
            {t("attributePanel.relationTypeCurrent", { label: labelFor(relationTypeId) })}
          </option>
        )}
        {forward.map((id) => (
          <option key={`fwd-${id}`} value={id}>
            {labelFor(id)}
          </option>
        ))}
        {reverse.length > 0 && (
          <optgroup label={t("attributePanel.reverseDirection")}>
            {reverse.map((id) => (
              <option key={`rev-${id}`} value={`${id}|reversed`}>
                {t("attributePanel.relationTypeReversed", { label: labelFor(id) })}
              </option>
            ))}
          </optgroup>
        )}
      </select>
      <p className="text-(--ld-muted) text-xs">
        <span className="font-mono">{source?.typeId.split(".")[1]}</span> →{" "}
        <span className="font-mono">{target?.typeId.split(".")[1]}</span>
        {": "}
        {t("attributePanel.forwardCount", { n: forward.length })}
        {reverse.length > 0 && t("attributePanel.reversedCountSuffix", { n: reverse.length })}
      </p>
    </div>
  );
}

/**
 * Adaptieve link-sectie voor drawing.Rectangle. Twee velden:
 *   1) Link-type-dropdown: "Geen" / "URL" / "Diagram"
 *   2) Bestemming, qua type afhankelijk van #1:
 *      - URL → text-input (placeholder "https://...")
 *      - Diagram → dropdown van alle diagrammen op de actieve branch
 *      - Geen → niets
 *
 * Linkermuisklik op de rechthoek op het canvas (Canvas.tsx onNodeClick)
 * navigeert dan naar de bestemming.
 */
function DrawingRectangleLinkSection({
  concept,
  workspaceId,
  branchId,
  lang: _lang,
}: {
  concept: Concept;
  workspaceId: string;
  branchId: string;
  lang: string;
}) {
  const { t } = useTranslation();
  // Subscribe op store-revision zodat de diagram-lijst meeloopt met
  // hydrate/rename/delete-events.
  // Subscribe op revision (number, stable comparison) zodat de
  // component re-rendert bij store-mutations. Diagrams lezen we
  // imperatief via getState() — een selector die `listDiagrams()`
  // direct returnt geeft elke render een nieuwe array-referentie
  // (Zustand vergelijkt met Object.is) → infinite re-render-loop
  // → React crash + white screen.
  const revision = useWorkspaceData((s) => s.revision);
  const diagrams = useMemo(
    () => useWorkspaceData.getState().store.listDiagrams(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [revision],
  );
  const attrs = concept.attributes ?? {};
  const linkKind = (attrs.link_kind ?? "none") as string;
  const linkTarget = (attrs.link_target ?? "") as string;

  const patch = (next: Record<string, unknown>) => {
    void useWorkspaceData.getState().patchConcept(workspaceId, branchId, concept.id, {
      attributes: { ...attrs, ...next },
    });
  };

  return (
    <section className="flex flex-col gap-2">
      <h3 className="font-semibold text-(--ld-text) text-xs">{t("attributePanel.link.title")}</h3>
      <label className="flex flex-col gap-0.5">
        <span className="text-(--ld-muted) text-xs">{t("attributePanel.sectionType")}</span>
        <select
          className="w-full rounded border border-(--ld-muted) bg-(--ld-surface) px-2 py-1 text-xs focus:border-(--ld-accent) focus:outline-none"
          value={linkKind}
          onChange={(e) => {
            // Bij wisseling kind clearen we ook target zodat een
            // achtergebleven URL niet als diagram-id wordt geïnterpreteerd
            // (en omgekeerd).
            patch({ link_kind: e.target.value, link_target: "" });
          }}
        >
          <option value="none">{t("attributePanel.link.none")}</option>
          <option value="url">URL</option>
          <option value="diagram">{t("attributePanel.headerDiagram")}</option>
        </select>
      </label>
      {linkKind === "url" && (
        <label className="flex flex-col gap-0.5">
          <span className="text-(--ld-muted) text-xs">{t("attributePanel.link.url")}</span>
          <input
            type="url"
            placeholder="https://..."
            value={linkTarget}
            onChange={(e) => patch({ link_target: e.target.value })}
            className="w-full rounded border border-(--ld-muted) bg-(--ld-surface) px-2 py-1 text-xs focus:border-(--ld-accent) focus:outline-none"
          />
        </label>
      )}
      {linkKind === "diagram" && (
        <label className="flex flex-col gap-0.5">
          <span className="text-(--ld-muted) text-xs">{t("attributePanel.headerDiagram")}</span>
          <select
            className="w-full rounded border border-(--ld-muted) bg-(--ld-surface) px-2 py-1 text-xs focus:border-(--ld-accent) focus:outline-none"
            value={linkTarget}
            onChange={(e) => patch({ link_target: e.target.value })}
          >
            <option value="">{t("attributePanel.chooseOption")}</option>
            {diagrams
              .slice()
              .sort((a, b) => {
                const an = (a.name?.nl ?? a.name?.en ?? "").toLowerCase();
                const bn = (b.name?.nl ?? b.name?.en ?? "").toLowerCase();
                return an.localeCompare(bn);
              })
              .map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name?.nl ?? d.name?.en ?? d.id}
                </option>
              ))}
          </select>
        </label>
      )}
    </section>
  );
}

/**
 * Dev-only debug-section onderaan het attributenpaneel. Toont de
 * technische data van het geselecteerde concept of relation:
 *  - de viewState-entry op het actieve diagram (positie, waypoints,
 *    angles, routing, containerId, ref, etc.) — kritisch voor
 *    bug-rapportages over canvas-render-issues
 *  - het entity-record uit de store (id, typeId, stereotypes,
 *    sourceConceptId/targetConceptId voor relaties)
 *
 * Alleen zichtbaar wanneer `import.meta.env.DEV === true` (Vite-flag
 * tijdens `pnpm dev`). In de productie-Netlify-build vervalt 't dood
 * code en wordt 'ie er door tree-shaking volledig uit gegooid.
 *
 * Klik "Copy" om de JSON in 't klembord te krijgen — handig om bij
 * een bug-report direct in chat te plakken.
 */
function DebugTechnicalSection({
  kind,
  entityId,
}: {
  kind: "concept" | "relation";
  entityId: string;
}) {
  // Vite-only flag — Webpack/jest hebben `import.meta.env` niet.
  // typeof-check voorkomt SSR/test-failures als 'ie er niet is.
  const isDev =
    typeof import.meta !== "undefined" &&
    typeof import.meta.env !== "undefined" &&
    import.meta.env.DEV === true;
  const revision = useWorkspaceData((s) => s.revision);
  void revision; // dependency-bait — re-render bij elke store-mutatie
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!isDev) return null;

  const ws = useWorkspaceData.getState();
  const activeDiagramId = ws.activeDiagramId;
  const diagram = activeDiagramId ? ws.store.getDiagram(activeDiagramId) : null;
  const viewStateEntry = diagram?.viewState[entityId];

  const entity =
    kind === "concept" ? ws.store.getConcept(entityId) : ws.store.getRelation(entityId);

  const payload = {
    kind,
    entityId,
    activeDiagramId,
    viewStateEntry: viewStateEntry ?? null,
    entity: entity ?? null,
  };
  const json = JSON.stringify(payload, null, 2);

  const onCopy = () => {
    try {
      void navigator.clipboard.writeText(json);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // navigator.clipboard kan blocked zijn (insecure context / iframe).
      // Silent — de gebruiker kan altijd manueel selecteren + Cmd+C.
    }
  };

  return (
    <section className="rounded border border-transparent bg-(--ld-notice-bg) p-2">
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="font-semibold text-xs text-(--ld-notice-ink) hover:text-(--ld-notice-ink)"
        >
          {expanded ? "▾" : "▸"} dev · technical info
        </button>
        {expanded && (
          <button
            type="button"
            onClick={onCopy}
            className="rounded border border-transparent bg-(--ld-surface) px-1.5 py-0.5 text-xs text-(--ld-notice-ink) hover:bg-(--ld-notice-bg)"
          >
            {copied ? "✓ Copied" : "Copy JSON"}
          </button>
        )}
      </div>
      {expanded && (
        <pre className="mt-1 max-h-72 overflow-auto whitespace-pre-wrap break-all rounded bg-(--ld-surface) p-1.5 font-mono text-xs text-(--ld-text)">
          {json}
        </pre>
      )}
    </section>
  );
}

/**
 * Stereotype-attribuut-bundels (M8). Elk actief stereotype met attributen
 * krijgt z'n eigen bundle. Bij ≥2 bundles worden ze als tabs getoond (één
 * tab per stereotype), bij precies 1 als enkele sectie met kop. Zo blijven
 * de attribuut-groepen van bv. een multi-stereotype Application Component
 * («C4 Container» + «Classified Data») logisch gescheiden.
 */
function StereotypeBundleTabs({
  concept,
  workspaceId,
  branchId,
  lang,
  stewardEditable,
}: {
  concept: Concept;
  workspaceId: string | null;
  branchId: string | null;
  lang: string;
  stewardEditable: boolean;
}) {
  const reg = getRegistry();
  const [active, setActive] = useState<string | null>(null);

  const bundles = concept.stereotypes
    .map((id) => {
      const st = reg.getStereotype(id);
      return st ? { id, st, attrs: reg.resolveStereotypeAttributes(st) } : null;
    })
    .filter((b): b is NonNullable<typeof b> => b !== null && b.attrs.length > 0);

  const first = bundles[0];
  if (!first) return null;
  const activeId = bundles.some((b) => b.id === active) ? (active as string) : first.id;
  const activeBundle = bundles.find((b) => b.id === activeId) ?? first;

  const renderField = (schema: (typeof activeBundle.attrs)[number]) => {
    // Virtuele opslag — definition_text / description_text delen de kernel-
    // `concept.description`-slot.
    if (schema.storage_mode === "concept_description") {
      return (
        <AttributeField
          key={schema.name}
          schema={schema}
          value={concept.description}
          disabled={schema.owner_only && !stewardEditable}
          lang={lang}
          onChange={(value) => {
            if (!workspaceId || !branchId) return;
            const next =
              typeof value === "string"
                ? { ...concept.description, nl: value }
                : (value as { nl?: string; en?: string });
            void useWorkspaceData.getState().patchConcept(workspaceId, branchId, concept.id, {
              description: { ...concept.description, ...next },
            });
          }}
        />
      );
    }
    if (schema.storage_mode === "concept_name") {
      return (
        <AttributeField
          key={schema.name}
          schema={schema}
          value={concept.name}
          disabled={schema.owner_only && !stewardEditable}
          lang={lang}
          onChange={(value) => {
            if (!workspaceId || !branchId) return;
            const next =
              typeof value === "string"
                ? { ...concept.name, nl: value }
                : (value as { nl?: string; en?: string });
            void useWorkspaceData.getState().patchConcept(workspaceId, branchId, concept.id, {
              name: { ...concept.name, ...next },
            });
          }}
        />
      );
    }
    // Manifestatie-niveau volgt uit het specialisatieprofiel, niet uit een
    // handmatige keuze — één `archimate.Function` dekt sinds ArchiMate 4 zowel
    // business als applicatie, dus alleen het profiel weet het nog. Read-only
    // tonen wanneer 't af te leiden is; valt de derivatie op `undefined`
    // (born-v4 zonder profiel) dan blijft het veld gewoon invulbaar in plaats
    // van een gok vast te leggen.
    if (schema.name === "manifestation_level") {
      const derived = deriveManifestationLevel(concept.typeId, concept.stereotypes);
      if (derived) {
        return (
          <AttributeField
            key={schema.name}
            schema={schema}
            value={derived}
            disabled
            lang={lang}
            ownerConcept={concept}
            onChange={() => {
              /* afgeleid — niet handmatig te wijzigen */
            }}
          />
        );
      }
    }
    return (
      <AttributeField
        key={schema.name}
        schema={schema}
        value={concept.attributes[schema.name]}
        disabled={schema.owner_only && !stewardEditable}
        lang={lang}
        ownerConcept={concept}
        workspaceId={workspaceId ?? undefined}
        branchId={branchId ?? undefined}
        onChange={(value) => {
          if (!workspaceId || !branchId) return;
          // Stereotype-derivation (bv. cvss_score → cvss_severity) atomic
          // meesturen met de user-edit.
          const derived = deriveDependentAttributes(activeBundle.id, schema.name, value);
          void useWorkspaceData.getState().patchConcept(workspaceId, branchId, concept.id, {
            attributes: { ...concept.attributes, [schema.name]: value, ...derived },
          });
        }}
      />
    );
  };

  return (
    <section className="flex flex-col gap-2">
      {bundles.length > 1 ? (
        <div className="flex flex-wrap gap-1 border-(--ld-border) border-b" role="tablist">
          {bundles.map((b) => (
            <button
              key={b.id}
              type="button"
              role="tab"
              aria-selected={b.id === activeId}
              onClick={() => setActive(b.id)}
              className={`-mb-px rounded-t border border-b-0 px-2 py-1 text-xs ${
                b.id === activeId
                  ? "border-(--ld-border) bg-(--ld-surface) font-medium text-(--ld-text)"
                  : "border-transparent text-(--ld-muted) hover:text-(--ld-text)"
              }`}
            >
              {resolveBilingual(b.st.label, lang)}
            </button>
          ))}
        </div>
      ) : (
        <h3 className="font-semibold text-(--ld-text) text-xs">
          {resolveBilingual(activeBundle.st.label, lang)}
        </h3>
      )}
      <div className="flex flex-col gap-2">{activeBundle.attrs.map(renderField)}</div>
    </section>
  );
}
